/**
 * Every sentence and number format the Profitability page and the duty board's
 * pay flags show. The engine returns statuses and amounts; turning them into
 * words lives here, next to the translations.
 */

import { getLocale } from '../../i18n';
import { addDaysToDateKey } from '../../services/payroll/time';
import type {
  ContractMargin,
  ContractScenario,
  MarginStatus,
  PayScenario,
  PayTrigger,
  PayTriggerKind,
  PreviousRates,
  ProfitabilityPeriod,
  ScenarioOutcome,
  ScenarioReport,
} from '../../services/profitability';

type Translate = (key: string, params?: Record<string, string | number>) => string;
type TranslatePlural = (singular: string, plural: string, count: number, params?: Record<string, string | number>) => string;

export interface Translators {
  t: Translate;
  tn: TranslatePlural;
}

export type PeriodKey = 'last_4_weeks' | 'next_4_weeks' | 'last_13_weeks';

export const PERIOD_KEYS: PeriodKey[] = ['last_4_weeks', 'next_4_weeks', 'last_13_weeks'];

/** The browser's own date, not UTC's: just after midnight the two disagree. */
export function localDateKey(now = new Date()): string {
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Whole weeks ending today, or starting today. The roster read allows four months at most. */
export function periodRange(key: PeriodKey, today: string): ProfitabilityPeriod {
  if (key === 'next_4_weeks') return { startDate: today, endDate: addDaysToDateKey(today, 27) };
  if (key === 'last_13_weeks') return { startDate: addDaysToDateKey(today, -90), endDate: today };
  return { startDate: addDaysToDateKey(today, -27), endDate: today };
}

export function periodLabel(key: PeriodKey, t: Translate): string {
  if (key === 'next_4_weeks') return t('Next 4 weeks');
  if (key === 'last_13_weeks') return t('Last 13 weeks');
  return t('Last 4 weeks');
}

export function formatMoney(value: number | null | undefined, currencyCode: string, fallback = '--'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  try {
    return new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // An unrecognised currency code must not take the page down.
    return `${currencyCode} ${value.toFixed(2)}`;
  }
}

/** Whole units for headline figures, where cents are noise. */
export function formatMoneyRounded(value: number | null | undefined, currencyCode: string, fallback = '--'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  try {
    return new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currencyCode} ${Math.round(value)}`;
  }
}

export function formatPercent(value: number | null | undefined, fallback = '--'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  return `${new Intl.NumberFormat(getLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)}%`;
}

/** A change in percentage points or percent, always signed. */
export function formatSignedPercent(value: number | null | undefined, fallback = '--'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback;
  const formatted = new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(value);
  return `${formatted}%`;
}

export function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return new Intl.NumberFormat(getLocale(), { maximumFractionDigits: value >= 100 ? 0 : 1 }).format(value);
}

export function formatDuration(minutes: number, t: Translate): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes - (hours * 60));
  if (hours === 0) return t('{minutes}m', { minutes: rest });
  if (rest === 0) return t('{hours}h', { hours });
  return t('{hours}h {minutes}m', { hours, minutes: rest });
}

export function formatDate(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(getLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function statusLabel(status: MarginStatus, t: Translate): string {
  switch (status) {
    case 'loss': return t('Loss-making');
    case 'below_target': return t('Below target');
    case 'on_target': return t('On target');
    default: return t('No charge rate');
  }
}

export type Tone = 'danger' | 'warning' | 'success' | 'neutral';

export function statusTone(status: MarginStatus): Tone {
  switch (status) {
    case 'loss': return 'danger';
    case 'below_target': return 'warning';
    case 'on_target': return 'success';
    default: return 'neutral';
  }
}

export function outcomeLabel(outcome: ScenarioOutcome, t: Translate): string {
  switch (outcome) {
    case 'turns_loss_making': return t('Turns loss-making');
    case 'already_loss_making': return t('Already loss-making');
    case 'falls_below_target': return t('Falls below target');
    case 'stays_below_target': return t('Still below target');
    case 'stays_on_target': return t('Stays on target');
    default: return t('No charge rate');
  }
}

export function outcomeTone(outcome: ScenarioOutcome): Tone {
  switch (outcome) {
    case 'turns_loss_making':
    case 'already_loss_making':
      return 'danger';
    case 'falls_below_target':
    case 'stays_below_target':
      return 'warning';
    case 'stays_on_target':
      return 'success';
    default:
      return 'neutral';
  }
}

/** A contract with no client is the engine's own bucket, named in the reader's language. */
export function contractLabel(
  contract: Pick<ContractMargin | ContractScenario, 'contractId' | 'contractName'>,
  t: Translate,
): string {
  return contract.contractId === null ? t('Sites without a client') : contract.contractName;
}

/** What a scenario does, in one sentence a reader can repeat to a client. */
export function scenarioHeadline(
  report: ScenarioReport,
  scenario: PayScenario,
  currencyCode: string,
  { t, tn }: Translators,
): string {
  const rate = formatMoney(scenario.minimumHourlyRate, currencyCode);

  if (report.turnsLossMakingCount > 0) {
    return tn(
      'At {rate} an hour, {count} contract that makes money today would lose it.',
      'At {rate} an hour, {count} contracts that make money today would lose it.',
      report.turnsLossMakingCount,
      { rate },
    );
  }
  if (report.fallsBelowTargetCount > 0) {
    return tn(
      'At {rate} an hour no contract turns loss-making, but {count} falls below your target margin.',
      'At {rate} an hour no contract turns loss-making, but {count} fall below your target margin.',
      report.fallsBelowTargetCount,
      { rate },
    );
  }
  if (report.priced.labourCostChange === 0) {
    return t('At {rate} an hour nothing changes: nobody on this roster is paid less than that.', { rate });
  }
  return t('At {rate} an hour every priced contract keeps the margin status it has today.', { rate });
}

export function previousRatesLabel(previous: PreviousRates, t: Translate): string {
  return t('Before {date}', { date: formatDate(previous.changeDate) });
}

export function triggerLabel(kind: PayTriggerKind, t: Translate): string {
  switch (kind) {
    case 'minimum_shift_pay': return t('Minimum shift payment');
    case 'unsocial_premium': return t('Unsocial-hours premium');
    case 'sunday_premium': return t('Sunday premium');
    case 'public_holiday_premium': return t('Public holiday premium');
    case 'overtime': return t('Overtime');
    case 'double_time': return t('Double time');
    default: return t('Below the statutory minimum rate');
  }
}

/** Why one shift costs more, in words. Amounts are left to the caller, who may not be allowed to show them. */
export function triggerDetail(trigger: PayTrigger, t: Translate): string {
  switch (trigger.kind) {
    case 'minimum_shift_pay':
      return t('Paid {duration} more than rostered to reach the minimum shift', {
        duration: formatDuration(trigger.minutes, t),
      });
    case 'unsocial_premium':
      return t('{duration} in unsocial hours', { duration: formatDuration(trigger.minutes, t) });
    case 'sunday_premium':
      return t('{duration} on a Sunday', { duration: formatDuration(trigger.minutes, t) });
    case 'public_holiday_premium':
      return trigger.label
        ? t('{duration} on a public holiday ({holiday})', {
          duration: formatDuration(trigger.minutes, t),
          holiday: t(trigger.label),
        })
        : t('{duration} on a public holiday', { duration: formatDuration(trigger.minutes, t) });
    case 'overtime':
      return t('{duration} of overtime', { duration: formatDuration(trigger.minutes, t) });
    case 'double_time':
      return t('{duration} at double time', { duration: formatDuration(trigger.minutes, t) });
    default:
      return t('Pay band rate is under the statutory minimum and is lifted to it');
  }
}

function csvCell(value: string | number | null): string {
  if (value === null) return '';
  const text = typeof value === 'number' ? String(Math.round(value * 100) / 100) : value;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** One row per contract and per site, with the scenario beside today's figures when there is one. */
export function buildProfitabilityCsv(
  contracts: ContractMargin[],
  scenario: ScenarioReport | null,
  t: Translate,
): string {
  const scenarioByContract = new Map(scenario?.contracts.map((contract) => [contract.contractId, contract]) ?? []);
  const header = [
    t('Client'), t('Site'), t('Status'), t('Shifts'), t('Billable hours'), t('Paid hours'),
    t('Charge rate'), t('Revenue'), t('Gross pay'), t('Employer on-costs'), t('Labour cost'),
    t('Cost per billable hour'), t('Margin'), t('Margin %'),
    ...(scenario ? [t('Scenario labour cost'), t('Scenario margin'), t('Scenario margin %'), t('Charge rise to hold margin %')] : []),
  ];

  const rows: (string | number | null)[][] = [];
  contracts.forEach((contract) => {
    const contractScenario = scenarioByContract.get(contract.contractId);
    const lines = [
      { site: '', line: contract, after: contractScenario },
      ...contract.sites.map((site) => ({
        site: site.siteName,
        line: site,
        after: contractScenario?.sites.find((entry) => entry.siteId === site.siteId),
      })),
    ];

    lines.forEach(({ site, line, after }) => {
      rows.push([
        contractLabel(contract, t), site, statusLabel(line.status, t), line.shiftCount,
        line.billableHours, line.paidHours, line.averageChargeRate, line.revenue,
        line.grossPay, line.onCost, line.labourCost, line.costPerBillableHour,
        line.margin, line.marginPercent,
        ...(scenario
          ? [after?.after.labourCost ?? null, after?.after.margin ?? null, after?.after.marginPercent ?? null, after?.upliftToHoldMargin ?? null]
          : []),
      ]);
    });
  });

  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}
